import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * LandingHero — the empty-chat landing for a new visitor.
 *
 * Ported from the native app's chat screen (sonmi-app-scan/src/app/chat.tsx)
 * so web and native meet people with the same first screen. Replaces the old
 * ~90-word welcome message + "Upload a selfie" card, which buried the scan CTA
 * under a wall of text explaining features nobody had asked about yet.
 *
 * The hero art is a line illustration rather than a photograph — deliberate, so
 * no visitor sees a specific skin tone standing in for theirs.
 */

const SERIF = 'var(--font-sonmi-display)';

export default function LandingHero({ onScan, prompts = [], onPick }) {
  const [promptsOpen, setPromptsOpen] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        paddingTop: 8,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Scan hero — clipped to a circle so the art sits flush on any surface */}
        <div
          style={{
            width: 190,
            height: 190,
            borderRadius: 999,
            overflow: 'hidden',
            background: '#FDF6EE',
            flexShrink: 0,
          }}
        >
          <img
            src="/sonmi-icon.png"
            alt=""
            aria-hidden="true"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
        </div>

        <h2
          style={{
            fontFamily: SERIF,
            fontSize: 31,
            fontWeight: 600,
            color: '#1A1A1A',
            margin: '22px 0 0',
            letterSpacing: '-0.005em',
          }}
        >
          Hi, I'm Son:mi
        </h2>
        <p
          style={{
            color: 'rgba(42,50,75,0.62)',
            fontSize: 15,
            lineHeight: '23px',
            textAlign: 'center',
            margin: '8px 0 0',
            maxWidth: 300,
          }}
        >
          Start with a 15-second skin scan.
        </p>

        {/* Primary action — the guided scan */}
        <button
          type="button"
          onClick={onScan}
          style={{
            marginTop: 34,
            alignSelf: 'stretch',
            border: 'none',
            borderRadius: 999,
            padding: '19px',
            background: 'linear-gradient(180deg, #4C2A22 0%, #2A1212 100%)',
            color: '#fff',
            fontSize: 17,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Analyse my skin
        </button>
        <p style={{ color: 'rgba(42,50,75,0.5)', fontSize: 13.5, margin: '12px 0 0' }}>
          or type any message below
        </p>
      </div>

      {/* Push the prompts to the bottom, matching native */}
      <div style={{ flex: 1, minHeight: 28 }} />

      {prompts.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setPromptsOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              alignSelf: 'flex-start',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'rgba(42,50,75,0.5)',
              fontSize: 13,
              fontFamily: 'inherit',
              marginBottom: promptsOpen ? 12 : 0,
            }}
          >
            Not sure what to ask?
            <ChevronDown
              size={15}
              style={{
                color: 'rgba(42,50,75,0.45)',
                transform: promptsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          </button>
          {promptsOpen && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {prompts.map((p) => {
                const label = typeof p === 'string' ? p : p.text;
                return (
                  <button
                    key={typeof p === 'string' ? p : p.id || label}
                    type="button"
                    onClick={() => onPick?.(typeof p === 'string' ? p : p.text)}
                    className="rounded-full border border-[#E1DBD2] bg-white px-3.5 py-2 hover:bg-[#FAF7F2] transition-colors"
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#2A1212',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
